/**
 * ClientSearchDropdown — MUI Autocomplete-based client search.
 *
 * Shared between the ora Remix app and the Outlook add-in via
 * app/common/workflows/components. Both apps bundle MUI v5.
 */
import { SyntheticEvent, useEffect, useRef, useState } from "react";
import {
  Autocomplete,
  createTheme,
  InputAdornment,
  TextField,
  ThemeProvider,
} from "@mui/material";
import type { ClientSearchResult } from "~/common/workflows/types/client";

// MUI theme values are colours, not class names, and this component is bundled by
// BOTH ora and the Outlook add-in — importing either app's token module (ora's
// getColorHexCodeByColorName / the add-in's src/tokens.ts) would pull that app's
// Tailwind config into the other's bundle. So the hexes stay literal here; each
// one is a Highstreet token and is named in the comment beside it.
//   #EAECF0 primary-gray-light-200   #D0D5DD primary-gray-light-300
//   #667085 primary-gray-light-500   rgba(16,24,40) primary-gray-light-900
//   #FFF3A5 lemon                    rgba(255,164,0) blossom
// #FFF8C9 (option hover) is the one value with no token — see the dropdown family
// in ora's dropdownTheme.ts, which uses the same shade.
const autocompleteTheme = createTheme({
  components: {
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: "0.5rem",
          border: "1px solid #EAECF0",
          boxShadow:
            "0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)",
        },
        listbox: {
          marginTop: "5px",
          padding: "6px",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": { borderRadius: "10px", background: "#EAECF0" },
          "&::-webkit-scrollbar-track": { borderRadius: "10px" },
        },
        option: {
          backgroundColor: "transparent !important",
          "&:hover": { backgroundColor: "#FFF8C9 !important" },
          "&[aria-selected='true']": { backgroundColor: "#FFF3A5 !important" },
        },
        inputRoot: {
          paddingLeft: "8px !important",
          paddingRight: "8px !important",
          paddingTop: "8px !important",
          paddingBottom: "8px !important",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "& fieldset": { borderRadius: "0.5rem !important" },
          "&.Mui-focused fieldset": {
            borderColor: "#D0D5DD !important",
            borderStyle: "solid",
            borderWidth: "1px",
            boxShadow: "0px 0px 2px 2px rgba(255, 164, 0, 0.24) !important",
          },
          "&:hover fieldset": {
            borderColor: "#D0D5DD !important",
            borderStyle: "solid",
            borderWidth: "1px",
            boxShadow: "0px 0px 2px 2px rgba(255, 164, 0, 0.24) !important",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: { input: { fontWeight: "normal" } },
    },
    MuiPopper: {
      styleOverrides: { root: { marginTop: "5px" } },
    },
  },
});

const SearchIcon = () => (
  <svg viewBox="0 0 25 24" className="h-4 w-4" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21.5 21L15.5001 15M17.5 10C17.5 13.866 14.366 17 10.5 17C6.63401 17 3.5 13.866 3.5 10C3.5 6.13401 6.63401 3 10.5 3C14.366 3 17.5 6.13401 17.5 10Z"
      stroke="#667085"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface ClientSearchDropdownProps {
  searchClients: (query: string) => Promise<ClientSearchResult[]>;
  onSelect: (result: ClientSearchResult) => void;
  placeholder?: string;
}

export default function ClientSearchDropdown({
  searchClients,
  onSelect,
  placeholder = "Search client name…",
}: ClientSearchDropdownProps) {
  const [value, setValue] = useState<ClientSearchResult | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<ClientSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeSearchRef = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleValueChange = (_event: SyntheticEvent, newValue: ClientSearchResult | null) => {
    setValue(newValue);
    if (newValue) onSelect(newValue);
  };

  const handleInputChange = (_event: SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!newInputValue || newInputValue.length < 3) {
      setOptions([]);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setOptions([]);
      setLoading(true);
      const searchId = ++activeSearchRef.current;
      try {
        const results = await searchClients(newInputValue);
        if (searchId === activeSearchRef.current) setOptions(results);
      } catch {
        // keep options empty
      } finally {
        if (searchId === activeSearchRef.current) setLoading(false);
      }
    }, 300);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <ThemeProvider theme={autocompleteTheme}>
      <Autocomplete
        value={value}
        size="small"
        onChange={handleValueChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        options={options}
        getOptionLabel={(option) => option.clientName}
        getOptionKey={(option) => option.clientSk}
        filterOptions={(x) => x}
        renderOption={(props, option) => (
          <li {...props} key={option.clientSk} className="mx-1 flex touch-target items-center rounded-md px-4 py-2 hover:bg-[#FFF8C9] cursor-pointer">
            <div className="flex w-full min-w-0 flex-row items-center gap-2">
              <span className="min-w-0 truncate font-semibold text-primary-gray-light-900 text-xs">{option.clientName}</span>
              {option.branchName && (
                <span className="flex-shrink-0 text-xs text-primary-gray-light-500">{option.branchName}</span>
              )}
            </div>
          </li>
        )}
        clearOnEscape
        selectOnFocus={false}
        loading={loading}
        className="w-full"
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder={placeholder}
            variant="outlined"
            onKeyDown={handleKeyDown}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </ThemeProvider>
  );
}
